import React, { useEffect, useMemo, useRef, useState } from "react";

const FUNCTION_BASE_URL =
  "https://us-central1-xeroo-farmacias-moscovich.cloudfunctions.net";

const ENDPOINTS = {
  createMeeting: `${FUNCTION_BASE_URL}/createMeeting`,
  startMeeting: `${FUNCTION_BASE_URL}/startMeeting`,
  realtimeSession: `${FUNCTION_BASE_URL}/createRealtimeTranscriptionSession`,
  appendTranscript: `${FUNCTION_BASE_URL}/appendMeetingTranscript`,
  analyze: `${FUNCTION_BASE_URL}/analyzeMeetingIncremental`,
  completeSuggestion: `${FUNCTION_BASE_URL}/completeMeetingSuggestion`,
  finishMeeting: `${FUNCTION_BASE_URL}/finishMeeting`,
  getMeeting: `${FUNCTION_BASE_URL}/getMeeting`,
};

const TRANSCRIPTION_USD_PER_MINUTE = 0.017;
const AI_INPUT_USD_PER_1M = 5;
const AI_OUTPUT_USD_PER_1M = 30;

export default function MeetingTranscriber() {
  const [meetingId, setMeetingId] = useState("");
  const [status, setStatus] = useState("idle");
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState("");

  const [meetingSeconds, setMeetingSeconds] = useState(0);
  const [aiCostUsd, setAiCostUsd] = useState(0);
  const [importantNotes, setImportantNotes] = useState("");

  const [form, setForm] = useState({
    title: "",
    clientName: "",
    projectName: "",
    clientContext: "",
    projectContext: "",
    expectedGoal: "",
    currentSystems: "",
    possibleIntegrations: "",
    notes: "",
  });

  const [partialText, setPartialText] = useState("");
  const [finalText, setFinalText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [meetingResult, setMeetingResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const streamRef = useRef(null);

  const pendingFinalBufferRef = useRef("");
  const livePartialRef = useRef("");
  const finalTextRef = useRef("");

  const analyzeIntervalRef = useRef(null);
  const autoFlushIntervalRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  const transcriptionCostUsd = useMemo(
    () => (meetingSeconds / 60) * TRANSCRIPTION_USD_PER_MINUTE,
    [meetingSeconds]
  );

  const totalCostUsd = transcriptionCostUsd + aiCostUsd;

  useEffect(() => {
    if (!isConnected) return;
    const timer = setInterval(() => setMeetingSeconds((prev) => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [isConnected]);

  function updateForm(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function setFinalTextSafe(updater) {
    setFinalText((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      finalTextRef.current = next;
      return next;
    });
  }

  async function postJson(url, body = {}) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) {
      throw new Error(JSON.stringify(data, null, 2));
    }
    return data;
  }

  async function createMeetingIfNeeded() {
    if (meetingId) return meetingId;

    setStatus("creating_meeting");

    const data = await postJson(ENDPOINTS.createMeeting, {
      ...form,
      importantNotes,
    });

    setMeetingId(data.meetingId);
    return data.meetingId;
  }

  async function startTranscription() {
    try {
      setError("");
      setMeetingResult(null);

      const id = await createMeetingIfNeeded();
      await postJson(ENDPOINTS.startMeeting, { meetingId: id });

      setStatus("creating_realtime_session");

      const sessionData = await postJson(ENDPOINTS.realtimeSession, {
        language: "es",
      });

      const ephemeralKey =
        sessionData?.client_secret?.value || sessionData?.value;

      if (!ephemeralKey) throw new Error("No se recibió ephemeral key");

      setStatus("requesting_microphone");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      setStatus("connecting_webrtc");

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;

      dc.onopen = () => {
        setIsConnected(true);
        setStatus("active");
        startAutoAnalysis(id);
        startAutoFlush(id);
      };

      dc.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          if (
            data.type === "conversation.item.input_audio_transcription.delta"
          ) {
            const delta = data.delta || "";
            livePartialRef.current += delta;
            setPartialText(livePartialRef.current);
          }

          if (
            data.type ===
            "conversation.item.input_audio_transcription.completed"
          ) {
            const transcript = String(data.transcript || "").trim();

            if (transcript) {
              setFinalTextSafe((prev) => prev + transcript + "\n");
              pendingFinalBufferRef.current += transcript + "\n";
              scheduleSaveTranscript(id);
            }

            livePartialRef.current = "";
            setPartialText("");
          }

          if (data.type === "error") {
            setError(JSON.stringify(data.error || data, null, 2));
          }
        } catch (err) {
          console.error("DataChannel message error", err);
        }
      };

      dc.onerror = () => setError("Error en canal realtime");

      pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === "failed" ||
          pc.connectionState === "disconnected" ||
          pc.connectionState === "closed"
        ) {
          setIsConnected(false);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpRes = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          "Content-Type": "application/sdp",
        },
        body: offer.sdp,
      });

      if (!sdpRes.ok) throw new Error(await sdpRes.text());

      const answerSdp = await sdpRes.text();

      await pc.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Error desconocido");
      setStatus("error");
      stopTranscription(false);
    }
  }

  function scheduleSaveTranscript(id) {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => flushTranscript(id), 1500);
  }

  function startAutoFlush(id) {
    if (autoFlushIntervalRef.current) clearInterval(autoFlushIntervalRef.current);
    autoFlushIntervalRef.current = setInterval(() => flushTranscript(id), 15000);
  }

  async function flushTranscript(id = meetingId) {
    let text = pendingFinalBufferRef.current.trim();

    if (!text && livePartialRef.current.trim()) {
      text = livePartialRef.current.trim();
      livePartialRef.current = "";
      setPartialText("");

      setFinalTextSafe((prev) => {
        if (prev.includes(text)) return prev;
        return prev + text + "\n";
      });
    }

    if (!id || !text) return;

    pendingFinalBufferRef.current = "";

    try {
      await postJson(ENDPOINTS.appendTranscript, {
        meetingId: id,
        text,
        source: "final",
      });
    } catch (err) {
      console.error("Error saving transcript", err);
      pendingFinalBufferRef.current = text + "\n" + pendingFinalBufferRef.current;
      setError("No se pudo guardar una parte de la transcripción.");
    }
  }

  function estimateAiCost({ inputText = "", expectedOutputTokens = 1200 }) {
    const inputTokens = Math.ceil(inputText.length / 4);
    const inputCost = (inputTokens / 1_000_000) * AI_INPUT_USD_PER_1M;
    const outputCost = (expectedOutputTokens / 1_000_000) * AI_OUTPUT_USD_PER_1M;
    return inputCost + outputCost;
  }

  function startAutoAnalysis(id) {
    if (analyzeIntervalRef.current) clearInterval(analyzeIntervalRef.current);
    analyzeIntervalRef.current = setInterval(() => analyzeMeeting(id), 120000);
  }

  async function analyzeMeeting(id = meetingId) {
    if (!id || isAnalyzing) return;

    try {
      setIsAnalyzing(true);
      await flushTranscript(id);

      const estimatedCost = estimateAiCost({
        inputText: finalTextRef.current + importantNotes,
        expectedOutputTokens: 900,
      });

      await postJson(ENDPOINTS.analyze, {
        meetingId: id,
        importantNotes,
      });

      setAiCostUsd((prev) => prev + estimatedCost);
      await refreshMeeting(id);
    } catch (err) {
      console.error(err);
      setError("No se pudo analizar la reunión con IA.");
    } finally {
      setIsAnalyzing(false);
    }
  }

  async function refreshMeeting(id = meetingId) {
    if (!id) return;

    const url = `${ENDPOINTS.getMeeting}?meetingId=${encodeURIComponent(id)}`;
    const res = await fetch(url);
    const data = await res.json();

    if (!res.ok || data.ok === false) {
      throw new Error(JSON.stringify(data, null, 2));
    }

    setSuggestions(data.suggestions || []);

    if (data.meeting?.fullTranscript && !finalTextRef.current) {
      setFinalTextSafe(data.meeting.fullTranscript);
    }

    if (data.meeting?.summary) {
      setMeetingResult(data.meeting.summary);
    }
  }

  async function toggleSuggestionCompleted(suggestion) {
    try {
      await postJson(ENDPOINTS.completeSuggestion, {
        meetingId,
        suggestionId: suggestion.id,
        completed: !suggestion.completed,
      });

      await refreshMeeting(meetingId);
    } catch (err) {
      setError("No se pudo actualizar la sugerencia.");
    }
  }

  async function stopTranscription(markStatus = true) {
    try {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (analyzeIntervalRef.current) clearInterval(analyzeIntervalRef.current);
      if (autoFlushIntervalRef.current) clearInterval(autoFlushIntervalRef.current);

      saveTimeoutRef.current = null;
      analyzeIntervalRef.current = null;
      autoFlushIntervalRef.current = null;

      dcRef.current?.close();
      pcRef.current?.close();
      streamRef.current?.getTracks().forEach((track) => track.stop());

      await flushTranscript(meetingId);
    } catch (err) {
      console.error(err);
    }

    dcRef.current = null;
    pcRef.current = null;
    streamRef.current = null;

    setIsConnected(false);
    if (markStatus) setStatus("paused");
  }

  async function finishMeeting() {
    try {
      if (!meetingId) return;

      setIsFinishing(true);
      setError("");

      await stopTranscription(false);
      await flushTranscript(meetingId);

      const estimatedCost = estimateAiCost({
        inputText: finalTextRef.current + importantNotes,
        expectedOutputTokens: 3500,
      });

      const data = await postJson(ENDPOINTS.finishMeeting, {
        meetingId,
        importantNotes,
      });

      setAiCostUsd((prev) => prev + estimatedCost);
      setMeetingResult(data.result);
      setStatus("finished");

      await refreshMeeting(meetingId);
    } catch (err) {
      setError(err.message || "No se pudo finalizar la reunión");
    } finally {
      setIsFinishing(false);
    }
  }

  function copyText(text) {
    navigator.clipboard.writeText(text || "");
  }

  useEffect(() => {
    return () => {
      try {
        if (analyzeIntervalRef.current) clearInterval(analyzeIntervalRef.current);
        if (autoFlushIntervalRef.current) clearInterval(autoFlushIntervalRef.current);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        dcRef.current?.close();
        pcRef.current?.close();
        streamRef.current?.getTracks().forEach((track) => track.stop());
      } catch (_) {}
    };
  }, []);

  const canEditContext = !isConnected && status !== "finished";
  const clientTitle = form.clientName || "Nueva reunión";
  const projectSubtitle = form.projectName || "Discovery de solución a medida";

  return (
    <div style={styles.page}>
      <header style={styles.topHeader}>
        <div style={styles.appLogo}>
          <span style={styles.logoDot} />
          CRM
        </div>
        <div style={styles.more}>•••</div>
      </header>

      <div style={styles.layout}>
        <aside style={styles.leftSidebar}>
          <div style={styles.searchBox}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              style={styles.searchInput}
              placeholder="SEARCH ACCOUNTS"
              disabled
            />
          </div>

          <div style={styles.accountList}>
            <AccountItem active letter="X" name={clientTitle} category={projectSubtitle} />
            <AccountItem letter="N" name="Nueva propuesta" category="Discovery" />
            <AccountItem letter="M" name="Minutas guardadas" category="Reuniones" />
            <AccountItem letter="P" name="Prompts generados" category="Apps a medida" />
          </div>

          <button style={styles.viewAllButton}>▢ Ver reuniones</button>
        </aside>

        <main style={styles.mainPanel}>
          <section style={styles.hero}>
            <div style={styles.avatarBig}>
              {(form.clientName || "X").slice(0, 1).toUpperCase()}
            </div>

            <div style={styles.heroInfo}>
              <h1 style={styles.clientName}>{clientTitle}</h1>
              <div style={styles.clientMeta}>
                {projectSubtitle} <span>•</span> Reunión IA
              </div>
            </div>

            <div style={isConnected ? styles.meetingLive : styles.meetingSoon}>
              ⏱ {isConnected ? "Grabando" : status}
            </div>
          </section>

          <div style={styles.pipeline}>
            {["Contexto", "Transcripción", "Análisis", "Minuta", "Prompt"].map(
              (step, idx) => (
                <div
                  key={step}
                  style={{
                    ...styles.pipelineStep,
                    ...(idx === 1 && isConnected ? styles.pipelineStepActive : {}),
                    ...(status === "finished" ? styles.pipelineStepDone : {}),
                  }}
                >
                  {step}
                  <span style={styles.pipelineDot} />
                </div>
              )
            )}
          </div>

          {error && (
            <div style={styles.errorBox}>
              <strong>Error:</strong>
              <pre style={styles.errorPre}>{error}</pre>
            </div>
          )}

          <section style={styles.briefCard}>
            <div style={styles.cardTitleRow}>
              <h2 style={styles.cardTitle}>Meeting brief</h2>
              <span style={styles.timeRange}>{formatDuration(meetingSeconds)}</span>
            </div>

            <div style={styles.briefGrid}>
              <BriefItem
                icon="↗"
                title={`Transcripción: USD ${transcriptionCostUsd.toFixed(4)}`}
                text="Costo estimado realtime"
                tone="green"
              />
              <BriefItem
                icon="◇"
                title={`IA: USD ${aiCostUsd.toFixed(4)}`}
                text="Procesamiento estimado"
                tone="red"
              />
              <BriefItem
                icon="👤"
                title={`Total: USD ${totalCostUsd.toFixed(4)}`}
                text="API estimada"
                tone="blue"
              />
              <BriefItem
                icon="🚀"
                title={meetingId ? "Sesión creada" : "Sin iniciar"}
                text={meetingId || "Presioná iniciar reunión"}
                tone="purple"
              />
            </div>
          </section>

          <section style={styles.activityCard}>
            <div style={styles.cardTitleRow}>
              <div style={styles.activityTitleWrap}>
                <div style={styles.calendarIcon}>▣</div>
                <div>
                  <h2 style={styles.activityTitle}>Meeting context</h2>
                  <p style={styles.activityDate}>Configuración inicial</p>
                </div>
              </div>
              <button style={styles.todayButton}>Today</button>
            </div>

            <div style={styles.formGrid}>
              <Input label="Cliente / empresa" value={form.clientName} disabled={!canEditContext} onChange={(v) => updateForm("clientName", v)} placeholder="Ej: Sablecrest Robotics" />
              <Input label="Proyecto" value={form.projectName} disabled={!canEditContext} onChange={(v) => updateForm("projectName", v)} placeholder="Ej: CRM a medida" />
              <Input label="Título" value={form.title} disabled={!canEditContext} onChange={(v) => updateForm("title", v)} placeholder="Ej: Discovery inicial" />
              <Input label="Objetivo" value={form.expectedGoal} disabled={!canEditContext} onChange={(v) => updateForm("expectedGoal", v)} placeholder="Ej: Mejorar proceso comercial" />
            </div>

            <Textarea label="Contexto del cliente" value={form.clientContext} disabled={!canEditContext} onChange={(v) => updateForm("clientContext", v)} placeholder="Rubro, operación, equipos..." />
            <Textarea label="Contexto del proyecto" value={form.projectContext} disabled={!canEditContext} onChange={(v) => updateForm("projectContext", v)} placeholder="Qué se quiere construir, problemas actuales..." />

            <div style={styles.formGrid}>
              <Textarea label="Sistemas actuales" value={form.currentSystems} disabled={!canEditContext} onChange={(v) => updateForm("currentSystems", v)} placeholder="HubSpot, Excel, WhatsApp..." />
              <Textarea label="Integraciones posibles" value={form.possibleIntegrations} disabled={!canEditContext} onChange={(v) => updateForm("possibleIntegrations", v)} placeholder="WhatsApp, email, calendario..." />
            </div>

            <Textarea label="Notas previas" value={form.notes} disabled={!canEditContext} onChange={(v) => updateForm("notes", v)} placeholder="Todo dato previo que ayude a la IA..." />
          </section>

          <section style={styles.activityCard}>
            <div style={styles.actions}>
              {!isConnected ? (
                <button style={styles.primaryButton} onClick={startTranscription}>
                  {status === "paused" ? "Continuar transcripción" : "Iniciar reunión"}
                </button>
              ) : (
                <button style={styles.warningButton} onClick={() => stopTranscription(true)}>
                  Pausar transcripción
                </button>
              )}

              <button style={styles.secondaryButton} disabled={!meetingId || isAnalyzing} onClick={() => analyzeMeeting(meetingId)}>
                {isAnalyzing ? "Analizando..." : "Analizar ahora"}
              </button>

              <button style={styles.dangerButton} disabled={!meetingId || isFinishing} onClick={finishMeeting}>
                {isFinishing ? "Finalizando..." : "Finalizar reunión"}
              </button>
            </div>

            <h2 style={styles.activityHeading}>Activity</h2>

            <TimelineItem
              title="Transcripción final"
              text={finalText || "Cuando empieces a hablar, la transcripción final aparecerá acá..."}
            />

            <TimelineItem
              title="Transcripción parcial"
              text={partialText || "El texto parcial aparece en vivo mientras hablás..."}
              muted
            />
          </section>

          <section style={styles.activityCard}>
            <h2 style={styles.activityHeading}>Resultado final</h2>

            {!meetingResult ? (
              <div style={styles.empty}>Al finalizar se generará la minuta, próximos pasos y prompt de app.</div>
            ) : (
              <div style={styles.results}>
                <ResultBlock title="Resumen ejecutivo" text={meetingResult.executiveSummary} onCopy={() => copyText(meetingResult.executiveSummary)} />
                <ResultBlock title="Minuta" text={meetingResult.meetingMinutes} onCopy={() => copyText(meetingResult.meetingMinutes)} />
                <ListBlock title="Próximos pasos" items={meetingResult.nextSteps} />
                <ListBlock title="Requerimientos funcionales" items={meetingResult.functionalRequirements} />
                <ListBlock title="Integraciones" items={meetingResult.integrations} />
                <ListBlock title="Riesgos / dudas pendientes" items={meetingResult.risksAndPendingQuestions} />
                <ResultBlock title="Prompt para crear app" text={meetingResult.appPrompt} onCopy={() => copyText(meetingResult.appPrompt)} wide />
              </div>
            )}
          </section>
        </main>

        <aside style={styles.rightPanel}>
          <div style={styles.scoutCard}>
            <h2 style={styles.scoutTitle}>
              <span style={styles.scoutIcon}>◎</span>
              Ask Scout
            </h2>

            <div style={styles.micWrap}>
              <div style={styles.wave} />
              <button style={isConnected ? styles.micButtonLive : styles.micButton}>
                {isConnected ? "🎙" : "◉"}
              </button>
              <div style={styles.wave} />
            </div>

            <div style={styles.transcriptHeader}>
              <span>Transcript</span>
              <button style={styles.linkButton} onClick={() => refreshMeeting(meetingId)} disabled={!meetingId}>
                View all
              </button>
            </div>

            <div style={styles.assistantBox}>
              <div style={styles.assistantTop}>
                <span>ASSISTANT</span>
                <span style={styles.liveMini}>{isConnected ? "Live" : status}</span>
              </div>
              <p style={styles.assistantText}>
                {partialText || suggestions[0]?.detail || "Esperando conversación..."}
              </p>
              <div style={styles.checking}>Analizando contexto.</div>
            </div>
          </div>

          <div style={styles.scoutCard}>
            <h3 style={styles.sideTitle}>Notas importantes</h3>
            <textarea
              style={styles.importantNotes}
              value={importantNotes}
              onChange={(e) => setImportantNotes(e.target.value)}
              placeholder="Datos clave: presupuesto, prioridades, dudas, decisiones..."
            />
          </div>

          <div style={styles.scoutCard}>
            <div style={styles.transcriptHeader}>
              <h3 style={styles.sideTitle}>Tool calls</h3>
              <button style={styles.linkButton} onClick={() => analyzeMeeting(meetingId)} disabled={!meetingId || isAnalyzing}>
                Run
              </button>
            </div>

            {suggestions.length === 0 ? (
              <div style={styles.toolCall}>✓ check_transcript</div>
            ) : (
              <div style={styles.suggestionList}>
                {suggestions.map((s) => (
                  <div key={s.id} style={styles.suggestionCard}>
                    <div style={styles.suggestionTop}>
                      <span style={styles.badge}>{s.type}</span>
                      <span style={styles.priority}>{s.priority}</span>
                    </div>
                    <h4 style={styles.suggestionTitle}>{s.title}</h4>
                    <p style={styles.suggestionDetail}>{s.detail}</p>
                    <button style={s.completed ? styles.smallButton : styles.completeButton} onClick={() => toggleSuggestionCompleted(s)}>
                      {s.completed ? "Marcar pendiente" : "Completar"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function AccountItem({ letter, name, category, active }) {
  return (
    <div style={active ? styles.accountActive : styles.accountItem}>
      <div style={styles.accountLetter}>{letter}</div>
      <div style={styles.accountInfo}>
        <div style={styles.accountName}>{name}</div>
        <div style={styles.accountCategory}>{category}</div>
      </div>
      <span style={styles.greenDot} />
    </div>
  );
}

function BriefItem({ icon, title, text, tone }) {
  return (
    <div style={styles.briefItem}>
      <div style={{ ...styles.briefIcon, ...(styles[tone] || {}) }}>{icon}</div>
      <div>
        <div style={styles.briefTitle}>{title}</div>
        <div style={styles.briefText}>{text}</div>
      </div>
    </div>
  );
}

function TimelineItem({ title, text, muted }) {
  return (
    <div style={styles.timelineItem}>
      <div style={muted ? styles.timelineDotMuted : styles.timelineDot} />
      <div>
        <div style={styles.timelineTitle}>{title}</div>
        <div style={styles.timelineText}>{text}</div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, disabled }) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>
      <input style={styles.input} value={value} disabled={disabled} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Textarea({ label, value, onChange, placeholder, disabled }) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>
      <textarea style={styles.textarea} value={value} disabled={disabled} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function ResultBlock({ title, text, onCopy, wide }) {
  return (
    <div style={wide ? styles.resultBlockWide : styles.resultBlock}>
      <div style={styles.resultHeader}>
        <h3 style={styles.resultTitle}>{title}</h3>
        <button style={styles.smallButton} onClick={onCopy}>Copiar</button>
      </div>
      <div style={styles.resultText}>{text || "-"}</div>
    </div>
  );
}

function ListBlock({ title, items }) {
  return (
    <div style={styles.resultBlock}>
      <h3 style={styles.resultTitle}>{title}</h3>
      {!items || items.length === 0 ? (
        <div style={styles.emptySmall}>Sin datos</div>
      ) : (
        <ul style={styles.ul}>
          {items.map((item, idx) => (
            <li key={idx} style={styles.li}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  return [
    h > 0 ? String(h).padStart(2, "0") : null,
    String(m).padStart(2, "0"),
    String(s).padStart(2, "0"),
  ]
    .filter(Boolean)
    .join(":");
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#fbfcfe",
    color: "#111827",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Arial, sans-serif",
  },
  topHeader: {
    height: 66,
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 22px",
    background: "#fff",
  },
  appLogo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: 22,
    fontWeight: 900,
    color: "#111827",
  },
  logoDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    border: "4px solid #1e90ff",
    display: "inline-block",
  },
  more: {
    color: "#6b7280",
    fontSize: 22,
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "320px 1fr 430px",
    minHeight: "calc(100vh - 66px)",
  },
  leftSidebar: {
    borderRight: "1px solid #e5e7eb",
    padding: 28,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "#fff",
  },
  searchBox: {
    height: 52,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 14px",
    marginBottom: 22,
  },
  searchIcon: { color: "#9ca3af", fontSize: 18 },
  searchInput: {
    border: "none",
    outline: "none",
    width: "100%",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 1.6,
    color: "#6b7280",
    background: "transparent",
  },
  accountList: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  accountItem: {
    display: "grid",
    gridTemplateColumns: "46px 1fr 10px",
    gap: 14,
    alignItems: "center",
    padding: 12,
    borderRadius: 12,
  },
  accountActive: {
    display: "grid",
    gridTemplateColumns: "46px 1fr 10px",
    gap: 14,
    alignItems: "center",
    padding: 12,
    borderRadius: 12,
    background: "#f1f7ff",
    border: "1px solid #dbeafe",
  },
  accountLetter: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0b84ff",
    fontWeight: 900,
    background: "#fff",
  },
  accountName: {
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  accountCategory: {
    color: "#6b7280",
    fontSize: 13,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  greenDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#14b8a6",
  },
  viewAllButton: {
    height: 52,
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    fontWeight: 800,
    cursor: "pointer",
  },
  mainPanel: {
    padding: "48px 56px",
    borderRight: "1px solid #e5e7eb",
    overflow: "auto",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "78px 1fr auto",
    gap: 24,
    alignItems: "center",
    marginBottom: 28,
  },
  avatarBig: {
    width: 74,
    height: 74,
    borderRadius: 14,
    background: "#f1f7ff",
    border: "1px solid #dbeafe",
    color: "#0b84ff",
    fontSize: 34,
    fontWeight: 950,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  clientName: {
    fontSize: 54,
    letterSpacing: -2.5,
    margin: 0,
    lineHeight: 1,
    fontWeight: 950,
  },
  clientMeta: {
    marginTop: 10,
    color: "#6b7280",
    fontWeight: 700,
    display: "flex",
    gap: 10,
  },
  meetingSoon: {
    border: "1px solid #dbeafe",
    background: "#fff",
    color: "#2563eb",
    borderRadius: 12,
    padding: "14px 18px",
    fontWeight: 900,
    boxShadow: "0 8px 20px rgba(15,23,42,0.04)",
  },
  meetingLive: {
    border: "1px solid #99f6e4",
    background: "#ecfdf5",
    color: "#0f766e",
    borderRadius: 12,
    padding: "14px 18px",
    fontWeight: 900,
    boxShadow: "0 8px 20px rgba(20,184,166,0.10)",
  },
  pipeline: {
    height: 48,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    alignItems: "center",
    padding: "0 16px",
    marginBottom: 20,
    background: "#fff",
  },
  pipelineStep: {
    color: "#9ca3af",
    fontSize: 13,
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  pipelineStepActive: {
    color: "#14b8a6",
  },
  pipelineStepDone: {
    color: "#14b8a6",
  },
  pipelineDot: {
    width: 8,
    height: 8,
    border: "2px solid currentColor",
    borderRadius: "50%",
  },
  briefCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    background: "#fff",
    padding: 22,
    marginBottom: 24,
    boxShadow: "0 16px 36px rgba(15,23,42,0.04)",
  },
  cardTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 22,
  },
  cardTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 900,
  },
  timeRange: {
    color: "#6b7280",
    fontWeight: 800,
  },
  briefGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 26,
  },
  briefItem: {
    display: "grid",
    gridTemplateColumns: "36px 1fr",
    gap: 14,
    alignItems: "start",
  },
  briefIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
  },
  green: { background: "#ecfdf5", color: "#14b8a6" },
  red: { background: "#fff1f2", color: "#ef4444" },
  blue: { background: "#eff6ff", color: "#2563eb" },
  purple: { background: "#eef2ff", color: "#4f46e5" },
  briefTitle: {
    color: "#111827",
    fontWeight: 900,
    fontSize: 16,
  },
  briefText: {
    color: "#6b7280",
    fontWeight: 650,
    marginTop: 3,
    fontSize: 13,
  },
  activityCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    background: "#fff",
    padding: 22,
    marginBottom: 24,
    boxShadow: "0 16px 36px rgba(15,23,42,0.04)",
  },
  activityTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 18,
  },
  calendarIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: "#ecfdf5",
    color: "#14b8a6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: 22,
  },
  activityTitle: {
    margin: 0,
    fontWeight: 900,
    fontSize: 20,
  },
  activityDate: {
    margin: "4px 0 0",
    color: "#6b7280",
    fontWeight: 650,
  },
  todayButton: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#14b8a6",
    borderRadius: 10,
    padding: "12px 18px",
    fontWeight: 900,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
    marginBottom: 14,
  },
  label: {
    color: "#6b7280",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 1.1,
    fontWeight: 900,
  },
  input: {
    height: 44,
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "0 12px",
    outline: "none",
    fontWeight: 700,
    color: "#111827",
    background: "#fff",
  },
  textarea: {
    minHeight: 76,
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 12,
    outline: "none",
    fontWeight: 650,
    color: "#111827",
    resize: "vertical",
    fontFamily: "inherit",
    background: "#fff",
  },
  actions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  primaryButton: {
    border: "none",
    background: "#14b8a6",
    color: "#fff",
    borderRadius: 10,
    padding: "12px 16px",
    fontWeight: 900,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    borderRadius: 10,
    padding: "12px 16px",
    fontWeight: 900,
    cursor: "pointer",
  },
  warningButton: {
    border: "none",
    background: "#f59e0b",
    color: "#fff",
    borderRadius: 10,
    padding: "12px 16px",
    fontWeight: 900,
    cursor: "pointer",
  },
  dangerButton: {
    border: "none",
    background: "#ef4444",
    color: "#fff",
    borderRadius: 10,
    padding: "12px 16px",
    fontWeight: 900,
    cursor: "pointer",
  },
  activityHeading: {
    fontSize: 20,
    fontWeight: 900,
    margin: "0 0 20px",
  },
  timelineItem: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "22px 1fr",
    gap: 16,
    marginBottom: 20,
  },
  timelineDot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "#6b7280",
    marginTop: 5,
  },
  timelineDotMuted: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "#14b8a6",
    marginTop: 5,
  },
  timelineTitle: {
    fontWeight: 900,
    color: "#111827",
    marginBottom: 5,
  },
  timelineText: {
    color: "#4b5563",
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    fontWeight: 650,
  },
  rightPanel: {
    background: "#fff",
    padding: 32,
    overflow: "auto",
  },
  scoutCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 22,
    marginBottom: 24,
    boxShadow: "0 16px 36px rgba(15,23,42,0.04)",
  },
  scoutTitle: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: 28,
    margin: "0 0 28px",
    fontWeight: 950,
  },
  scoutIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "#14b8a6",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
  },
  micWrap: {
    display: "grid",
    gridTemplateColumns: "1fr 90px 1fr",
    alignItems: "center",
    gap: 12,
    marginBottom: 26,
  },
  wave: {
    height: 34,
    background:
      "repeating-linear-gradient(90deg, rgba(20,184,166,.12) 0 3px, transparent 3px 7px)",
    borderRadius: 999,
  },
  micButton: {
    width: 86,
    height: 86,
    borderRadius: "50%",
    border: "10px solid #ecfdf5",
    background: "#14b8a6",
    color: "#fff",
    fontSize: 30,
    cursor: "pointer",
  },
  micButtonLive: {
    width: 86,
    height: 86,
    borderRadius: "50%",
    border: "10px solid #ccfbf1",
    background: "#0f766e",
    color: "#fff",
    fontSize: 30,
    cursor: "pointer",
    boxShadow: "0 0 0 12px rgba(20,184,166,.08)",
  },
  transcriptHeader: {
    borderTop: "1px solid #e5e7eb",
    paddingTop: 18,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 900,
    color: "#6b7280",
    marginBottom: 14,
  },
  linkButton: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontWeight: 900,
    cursor: "pointer",
  },
  assistantBox: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 16,
  },
  assistantTop: {
    display: "flex",
    justifyContent: "space-between",
    color: "#6b7280",
    fontSize: 12,
    letterSpacing: 1.5,
    fontWeight: 900,
    marginBottom: 16,
  },
  liveMini: {
    background: "#ecfdf5",
    color: "#14b8a6",
    padding: "3px 8px",
    borderRadius: 999,
    letterSpacing: 0,
  },
  assistantText: {
    color: "#4b5563",
    lineHeight: 1.5,
    margin: 0,
    whiteSpace: "pre-wrap",
  },
  checking: {
    borderTop: "1px solid #e5e7eb",
    marginTop: 16,
    paddingTop: 14,
    color: "#9ca3af",
    fontSize: 12,
    fontWeight: 800,
  },
  importantNotes: {
    width: "100%",
    minHeight: 150,
    boxSizing: "border-box",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 12,
    outline: "none",
    resize: "vertical",
    fontWeight: 650,
    fontFamily: "inherit",
  },
  sideTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 900,
  },
  toolCall: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 14,
    fontWeight: 900,
    color: "#374151",
  },
  suggestionList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  suggestionCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 14,
  },
  suggestionTop: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  badge: {
    background: "#ecfdf5",
    color: "#14b8a6",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 900,
  },
  priority: {
    background: "#eff6ff",
    color: "#2563eb",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 900,
  },
  suggestionTitle: {
    margin: "0 0 6px",
    fontWeight: 900,
  },
  suggestionDetail: {
    color: "#6b7280",
    fontSize: 13,
    lineHeight: 1.45,
  },
  smallButton: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    borderRadius: 8,
    padding: "8px 10px",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 12,
  },
  completeButton: {
    border: "none",
    background: "#14b8a6",
    color: "#fff",
    borderRadius: 8,
    padding: "8px 10px",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 12,
  },
  results: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  resultBlock: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 14,
  },
  resultBlockWide: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 14,
    gridColumn: "1 / -1",
  },
  resultHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
  },
  resultTitle: {
    margin: 0,
    fontWeight: 900,
  },
  resultText: {
    color: "#4b5563",
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    fontSize: 13,
  },
  ul: { margin: 0, paddingLeft: 18 },
  li: {
    color: "#4b5563",
    lineHeight: 1.5,
    fontSize: 13,
    marginBottom: 5,
  },
  empty: {
    color: "#6b7280",
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 14,
    fontWeight: 650,
  },
  emptySmall: {
    color: "#6b7280",
  },
  errorBox: {
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    borderRadius: 10,
    padding: 14,
    marginBottom: 20,
  },
  errorPre: {
    margin: "8px 0 0",
    whiteSpace: "pre-wrap",
  },
};